import { SORT_ORDER_ASC, SORT_ORDER_DESC } from '@mode/charts/domain';
import * as $ from 'jquery';

import { elementData } from '../util';

const OPEN_DROPDOWN_STATE = 'open';
const CLOSED_DROPDOWN_STATE = 'closed';
const SORT_STATE_CLASS_NAMES = {
  [SORT_ORDER_ASC]: 'sort-asc',
  [SORT_ORDER_DESC]: 'sort-desc',
};
const DROPDOWN_BUTTON_CLASS_NAME = 'axel-column-sort-dropdown';
const DROPDOWN_BUTTON_TEMPLATE = `
  <div class="${DROPDOWN_BUTTON_CLASS_NAME}">
    <a href="javascript:void(0)" class="axel-dropdown dropdown active">
      <span
        class="action-menu axel-dropdown-toggle dropdown-toggle"
        aria-haspopup="true"
        aria-expanded="false"></span>
    </a>
  </div>
`;
const DROPDOWN_MENU_TEMPLATE = `
  <ul class="dropdown-menu axel-dropdown-menu offset-shadow">
    <li>
      <span class="button-link btn-asc js-btn-asc icon-sort-ascending" data-axel-order="asc">
        <i class="icon-check-mark align-md-icon"></i>
        <span class="button-label">Sort Ascending</span>
      </span>
    </li>
    <li>
      <span class="button-link btn-desc js-btn-desc icon-sort-descending" data-axel-order="desc">
        <i class="icon-check-mark align-md-icon "></i>
        <span class="button-label">Sort Descending</span>
      </span>
    </li>
    <li>
      <span class="button-link js-btn-clear" data-axel-order="clear">
        <i class="icon-check-mark align-md-icon "></i>
        <span class="button-label">No Sort</span>
      </span>
    </li>
  </ul>
`;

/**
 * Column Sort Plugin
 *
 * Appends dropdown menu element and delegates events that
 * trigger column sorting callbacks.
 */
export default class ColumnSort {
  static NAMESPACE = 'axel-column-sort';

  public label;
  private opts: {
    sortCol?: (index: number, order: string) => void;
    sortedCol?: () => void;
    selectedColumn?: () => void;
    onDropdownOpen?: (colIndex: number) => void;
    onDropdownClose?: (colIndex: number) => void;
    dropdownState?: (colIndex: number, open: boolean) => void;
  };
  private state;
  private $container;
  private $table;
  private $headerCells;
  private $dropdownMenu;
  private ui;
  private columnIndexOfOpenMenu: number;
  private closeMenu: () => void;

  constructor(_, opts = {}) {
    this.opts = opts;
    this.label = 'column-sort';
    this.ui = {
      itemAsc: '.js-btn-asc',
      itemDesc: '.js-btn-desc',
      itemClear: '.js-btn-clear',
    };
    this.state = {
      sorted: { index: null, order: 'desc' },
      selectedColumn: null,
      dropdownOpen: [0, null],
    };
  }

  /**
   * [HookMethod] Initialize
   * @param _
   * @param containerEl
   */
  public initialize(_, containerEl) {
    this.$container = $(containerEl);
  }

  /**
   * [HookMethod] Build Header
   *
   * Appends dropdown menu to each table header node
   * @param theadEl
   * @param opts
   * @returns {any}
   */
  public buildHeader(theadEl, opts: any = {}) {
    const $headerCells = (this.$headerCells = $(theadEl).find('th:not(.js-axel-ignore)'));

    $headerCells.each(this.initializeTableHeaderLabels.bind(this));
    $headerCells.each(this.initializeAndAppendDropdownButton.bind(this));
    return theadEl;
  }

  /**
   * [HookMethod] Build Table
   *
   * Delegates events and callbacks to table header node dropdowns
   * @param tableEl
   * @param containerEl
   * @param opts
   * @returns {any}
   */
  public buildTable(tableEl, containerEl, opts: any = {}) {
    const $table = (this.$table = $(tableEl));
    const $headerCells = this.$headerCells;

    this.$dropdownMenu = $(DROPDOWN_MENU_TEMPLATE).appendTo(this.$container.parents('body'));
    $headerCells.each(this.delegateDropdownToggleHandlers.bind(this));
    this.delegateDropdownMenuItemHandlers();
    // add plugin class
    $table.addClass(ColumnSort.NAMESPACE);
    return tableEl;
  }

  /**
   * [HookMethod] Destroy
   * @param _
   */
  public destroy(_) {
    this.$headerCells.each((i, thEl) => ColumnSort.findAndDestroyDropdownButton(thEl));
    this.$container.parents('body').off(this.namespace('mousedown'));
    this.$dropdownMenu.remove();
  }

  /**
   * Destroy Dropdown Menu
   *
   * Remove dropdown from dom and destroy events
   * @param $th
   */
  static findAndDestroyDropdownButton(thEl) {
    $(thEl).find(`.${DROPDOWN_BUTTON_CLASS_NAME}`).remove();
  }

  private initializeTableHeaderLabels(index, thEl) {
    const colIndex = parseInt(elementData(thEl, 'column'));
    const sortStateClassName = (currColIndex) => {
      const { index, order } = this.getColumnSortState();

      if (index === currColIndex) {
        return SORT_STATE_CLASS_NAMES[order];
      } else {
        return '';
      }
    };

    $(thEl).wrapInner('<span class="axel-table-header-label"></span>').addClass(sortStateClassName(colIndex));
  }

  /**
   * Initialize and Append Dropdown
   *
   * Creates dropdown HTML Node and appends to table header node
   * @todo refactor to use a proper view pattern instead of a enter-update-exit adhoc jquery based pattern
   * @todo create a method that
   * @param index
   * @param thEl
   */
  private initializeAndAppendDropdownButton(index, thEl) {
    const colIndex = parseInt(elementData(thEl, 'column'));

    $(thEl).addClass('axel-column-sort-header').append(DROPDOWN_BUTTON_TEMPLATE);
  }

  /**
   * Initialize dropdown sort state for given column
   *
   * @param template
   * @param colIndex
   * @returns {JQuery}
   */
  private initDropdownMenu(colIndex: number) {
    const selectedStateSelector = 'selected';
    const { index, order } = this.getColumnSortState();
    const state = {
      sorted: index === colIndex && (order === SORT_ORDER_ASC || order === SORT_ORDER_DESC),
    };
    const setDropdownState = ($el) => {
      if (state.sorted && order === SORT_ORDER_ASC) {
        $el.find(this.ui.itemAsc).addClass(selectedStateSelector);
      } else if (state.sorted && order === SORT_ORDER_DESC) {
        $el.find(this.ui.itemDesc).addClass(selectedStateSelector);
      }
    };

    setDropdownState(this.$dropdownMenu);
  }

  /**
   * Namespace
   * @param evtName
   * @returns {string}
   */
  private namespace(evtName) {
    return evtName + '.' + ColumnSort.NAMESPACE;
  }

  /**
   * Set Column Sort State
   *
   * Use provided column sort method or default to local state
   * @param index
   * @param order
   */
  private setColumnSortState(index, order) {
    if (this.opts.sortCol instanceof Function) {
      this.opts.sortCol.call(null, index, order);
    } else {
      this.state.selectedColumn = index;
      this.state.sorted.index = index;
      this.state.sorted.order = order;
    }
  }

  /**
   * Get Column Sort State
   *
   * Retrieve column sort state from callback or local state
   * @returns {{index, order}|any}
   */
  private getColumnSortState() {
    if (this.opts.sortedCol instanceof Function) {
      return this.opts.sortedCol.call(null);
    } else {
      return this.state.sorted;
    }
  }

  /**
   * Selected Column
   *
   * Retrieve selected column state from callback or local state
   * @todo remove since this is overlaps with the column select plugin
   * @returns {null}
   */
  private selectedColumn() {
    return this.opts.selectedColumn instanceof Function
      ? this.opts.selectedColumn.call(null)
      : this.state.selectedColumn;
  }

  /**
   * On Dropdown Open
   *
   * Overridable dropdown open event callback
   * @param colIndex
   * @returns {any}
   */
  private onDropdownOpen(colIndex) {
    if (this.opts.onDropdownOpen instanceof Function) {
      return this.opts.onDropdownOpen.call(null, colIndex);
    }
  }

  /**
   * On Dropdown Close
   *
   * Overridable dropdown close event callback
   * @param colIndex
   * @returns {any}
   */
  private onDropdownClose(colIndex) {
    if (this.opts.onDropdownClose instanceof Function) {
      return this.opts.onDropdownClose.call(null, colIndex);
    }
  }

  /**
   * Dropdown State
   *
   * Getter setter for dropdown UI state (open/closed)
   * @param colIndex
   * @param open
   * @returns {any}
   */
  private dropdownState(colIndex, open = null) {
    if (this.opts.dropdownState) {
      return this.opts.dropdownState.call(null, colIndex, open);
    } else {
      return arguments.length > 1 ? (this.state.dropdownOpen = [colIndex, open]) : this.state.dropdownOpen;
    }
  }

  /**
   * Is Open in Column
   *
   * Determines if dropdown is open for given column index
   * @param colIndex
   * @returns {boolean}
   */
  private isOpenInColumn(colIndex) {
    const ddState = this.dropdownState(colIndex);
    return ddState[0] === colIndex && ddState[1] === OPEN_DROPDOWN_STATE;
  }

  /**
   * Position Dropdown Menu
   *
   * Positions dropdown menu under the header cell
   * @param $container
   * @param $menu
   */
  private positionMenu($th, $menu) {
    const { bottom, right } = $th[0].getBoundingClientRect();
    const DROPDOWN_MENU_WIDTH = 160,
      DROPDOWN_BUTTON_RIGHT_MARGIN = 7,
      TH_RIGHT_BORDER = 1;
    $menu.css({
      top: Math.max(5, pageYOffset + bottom),
      left: Math.max(5, pageXOffset + right - DROPDOWN_MENU_WIDTH - DROPDOWN_BUTTON_RIGHT_MARGIN - TH_RIGHT_BORDER),
    });
  }

  /**
   * Delegate Dropdown Menu Functions
   *
   * Attaches callbacks to dropdown menu, trigger, and global document
   * body to control local and application state change
   * @todo factor out the dropdown menu functionality into another object or factory
   * @param index
   * @param el
   */
  private delegateDropdownToggleHandlers(index, el) {
    const namespace = this.namespace;
    const $container = this.$container;
    const $th = $(el);
    const columnIndex = elementData($th, 'column');
    const $trigger = $th.find('.dropdown');
    const $menu = this.$dropdownMenu;

    const exitMenuClickHandler = function (evt) {
      const containsTarget = ($el) => $.contains($el.get(0), evt.target);
      if (!containsTarget($menu) && !containsTarget($trigger)) {
        closeMenu();
      }
    };

    const openMenu = () => {
      if (this.selectedColumn() !== columnIndex) {
        $th.trigger('click');
      }
      this.initDropdownMenu(columnIndex);
      this.positionMenu($th, $menu);
      this.dropdownState(columnIndex, OPEN_DROPDOWN_STATE);
      $menu.addClass('dropdown-open');
      $container.parents('body').on(namespace('mousedown'), exitMenuClickHandler);
      this.onDropdownOpen(columnIndex);

      this.columnIndexOfOpenMenu = columnIndex;
      this.closeMenu = closeMenu;
    };

    const closeMenu = () => {
      this.dropdownState(columnIndex, CLOSED_DROPDOWN_STATE);
      $menu.removeClass('dropdown-open');
      $container.parents('body').off(namespace('mousedown'), exitMenuClickHandler);
      this.onDropdownClose(columnIndex);
    };

    const toggleMenu = () => (this.isOpenInColumn(columnIndex) ? closeMenu() : openMenu());

    $trigger.on(namespace('click'), (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      toggleMenu();
    });
  }

  private delegateDropdownMenuItemHandlers() {
    const namespace = this.namespace;
    const $menu = this.$dropdownMenu;

    const sortAscending = (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      this.setColumnSortState(this.columnIndexOfOpenMenu, SORT_ORDER_ASC);
      this.closeMenu();
    };

    const sortDescending = (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      this.setColumnSortState(this.columnIndexOfOpenMenu, SORT_ORDER_DESC);
      this.closeMenu();
    };

    const sortClear = (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      this.setColumnSortState(null, null);
      this.closeMenu();
    };

    $menu.find('.js-btn-asc').on(namespace('click'), sortAscending);
    $menu.find('.js-btn-desc').on(namespace('click'), sortDescending);
    $menu.find('.js-btn-clear').on(namespace('click'), sortClear);
  }
}
